/// <reference path="../apple-pay.d.ts" />
'use client';

import { useEffect, useRef } from 'react';

import { useRouter } from 'next/navigation';

import { useTranslation } from 'react-i18next';

import { toMinorUnits } from '@kit/shared/currency';
import { useTenantCurrency } from '@kit/ui/currency-icon';
import { buildPaymentDescription } from '@kit/payments/description';
import { toast } from '@kit/ui/sonner';

interface ApplePayMembershipPurchaseButtonProps {
  plan: {
    id: string;
    name: string;
    /** The amount to charge — the plan price less any discount code. */
    price: number;
    period: string;
  };
  /** Extra payment metadata, e.g. the discount code the confirm paths re-validate. */
  extraMetadata?: Record<string, string> | null;
  /** Client-generated purchase reference (MEM-…) binding the payment to this checkout. */
  purchaseId: string;
  publishableKey: string;
  /** The buyer — confirm/webhook require a non-empty user_id to materialize the membership. */
  userId: string;
  tenantId?: string;
  tenantName?: string;
  /** Plan's location_id — routes Apple Pay to the per-location merchant. */
  locationId?: string;
  onPaymentStart?: () => void;
  /** Fires only on a successful charge, right before navigation to the confirm page. */
  onPaymentSuccess?: () => void;
  onPaymentComplete?: () => void;
}

function formatPaymentError(
  raw: string,
  t: (key: string, options?: { defaultValue: string }) => string,
): string {
  const normalized = raw.toUpperCase();

  if (normalized.includes('CARD COUNTRY NOT ALLOWED') || normalized.includes('CARD_COUNTRY')) {
    return t('common:memberClub.payment.errorCardCountry', {
      defaultValue: 'Please use a Saudi-issued card or call us to confirm your reservation.',
    });
  }

  if (normalized.includes('INSUFFICIENT_FUNDS') || normalized.includes('INSUFFICIENT FUNDS')) {
    return t('common:memberClub.payment.errorInsufficientFunds', {
      defaultValue: 'Insufficient funds. Please try a different card.',
    });
  }

  if (normalized.includes('EXPIRED') || normalized.includes('CARD_EXPIRED')) {
    return t('common:memberClub.payment.errorCardExpired', {
      defaultValue: 'This card has expired. Please use a different card.',
    });
  }

  if (normalized.includes('BLOCKED') && !normalized.includes('CARD COUNTRY')) {
    return t('common:memberClub.payment.errorCardBlocked', {
      defaultValue: 'This card has been blocked. Please try a different card.',
    });
  }

  return raw;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function safeCompletePayment(session: any, status: number) {
  try {
    session.completePayment({ status });
  } catch {
    // Silently ignore — the Apple Pay sheet will close regardless
  }
}

export function ApplePayMembershipPurchaseButton({
  plan,
  extraMetadata,
  purchaseId,
  publishableKey,
  userId,
  tenantId,
  tenantName,
  locationId,
  onPaymentStart,
  onPaymentSuccess,
  onPaymentComplete,
}: ApplePayMembershipPurchaseButtonProps) {
  const router = useRouter();
  const tenantCurrency = useTenantCurrency();
  const { t } = useTranslation();
  const applePayButtonRef = useRef<HTMLElement | null>(null);

  const handleApplePayClick = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!(window as any).ApplePaySession) {
      toast.error(
        t('common:memberClub.payment.toastApplePayUnavailable', {
          defaultValue: 'Apple Pay is not available on this device',
        }),
      );
      return;
    }

    if (onPaymentStart) {
      onPaymentStart();
    }

    // Moyasar is a Saudi PSP — settles in SAR only. Forced to SAR regardless
    // of tenant's display currency. PSP currency gating belongs at the tenant
    // currency-picker level (`apps/web/app/api/currencies/route.ts`).
    if (tenantCurrency !== 'SAR') {
      console.warn(
        `[Apple Pay] Tenant displays ${tenantCurrency} but Moyasar will charge SAR — amounts diverge (no FX conversion applied)`,
      );
    }
    const applePayPaymentRequest = {
      countryCode: 'SA',
      currencyCode: 'SAR',
      supportedNetworks: ['mada', 'visa', 'masterCard'],
      merchantCapabilities: ['supports3DS', 'supportsDebit', 'supportsCredit'],
      total: {
        label: t('common:memberClub.payment.applePayLineLabel', {
          defaultValue: '{{packageName}} - Payment',
          packageName: plan.name,
        }),
        amount: plan.price.toFixed(2),
      },
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const session = new (window as any).ApplePaySession(5, applePayPaymentRequest);

    let merchantValidated = false;
    let paymentFailed = false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    session.onvalidatemerchant = async (event: any) => {
      const body = {
        validation_url: event.validationURL,
        display_name: 'Membership Purchase',
        domain_name: window.location.hostname,
        publishable_api_key: publishableKey,
      };

      try {
        const response = await fetch(
          'https://api.moyasar.com/v1/applepay/initiate',
          {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          },
        );

        const merchantSession = await response.json();
        session.completeMerchantValidation(merchantSession);
        merchantValidated = true;
      } catch (error) {
        console.error('Merchant validation error:', error);
        toast.error(
          t('common:memberClub.payment.toastPaymentValidationFailed', {
            defaultValue: 'Payment validation failed',
          }),
        );
        session.abort();
        if (onPaymentComplete) onPaymentComplete();
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    session.onpaymentauthorized = async (event: any) => {
      const token = event.payment.token;

      try {
        // Metadata mirrors MoyasarCheckoutDialog's membership_purchase branch —
        // the webhook classifier and checkMembershipPurchasePaymentStatusAction
        // require type/plan_id/user_id (period, purchase_id, location_id are
        // read by the materializer). No row exists yet: the membership is
        // materialized on confirm from exactly these keys.
        const paymentBody = {
          amount: toMinorUnits(plan.price, 'SAR'),
          currency: 'SAR',
          description: buildPaymentDescription({
            surface: 'membership_purchase',
            name: plan.name,
          }),
          publishable_api_key: publishableKey,
          source: {
            type: 'applepay',
            token: token,
          },
          metadata: {
            type: 'membership_purchase',
            plan_id: plan.id,
            user_id: userId,
            period: plan.period,
            purchase_id: purchaseId,
            tenant_id: tenantId || '',
            tenant_name: tenantName || '',
            // Stamp location so the webhook resolves the per-location merchant.
            ...(locationId && { location_id: locationId }),
            ...extraMetadata,
          },
        };

        const paymentResponse = await fetch(
          'https://api.moyasar.com/v1/payments',
          {
            method: 'post',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(paymentBody),
          },
        );

        const paymentResult = await paymentResponse.json();

        if (!paymentResponse.ok || paymentResult.status !== 'paid') {
          const rawReason =
            paymentResult.message ||
            paymentResult.source?.message ||
            t('common:memberClub.payment.errorPaymentFailedFallback', {
              defaultValue: 'Payment failed',
            });
          const failureReason = formatPaymentError(rawReason, t);
          paymentFailed = true;
          toast.error(failureReason);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          safeCompletePayment(session, (window as any).ApplePaySession.STATUS_FAILURE);
          if (onPaymentComplete) onPaymentComplete();
          // No cleanup needed — nothing was written; the plan stays purchasable
          return;
        }

        // Payment successful
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        safeCompletePayment(session, (window as any).ApplePaySession.STATUS_SUCCESS);

        toast.success(
          t('common:memberClub.payment.toastPaymentSuccessVerifying', {
            defaultValue: 'Payment successful! Verifying...',
          }),
        );

        if (onPaymentSuccess) onPaymentSuccess();

        // The catalog's return-effect polls the confirm action off ?id= and
        // materializes the membership (same URL Moyasar card flow returns to).
        router.push(`/memberships?membership_purchase=1&id=${paymentResult.id}`);

        if (onPaymentComplete) onPaymentComplete();
      } catch (error) {
        console.error('Payment error:', error);
        paymentFailed = true;
        const errorMessage =
          error instanceof Error
            ? error.message
            : t('common:memberClub.payment.errorPaymentProcessing', {
                defaultValue: 'Payment processing failed',
              });
        toast.error(errorMessage);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        safeCompletePayment(session, (window as any).ApplePaySession.STATUS_FAILURE);
        if (onPaymentComplete) onPaymentComplete();
      }
    };

    session.oncancel = () => {
      if (paymentFailed) {
        return;
      }

      if (!merchantValidated) {
        console.error('[Apple Pay] Payment sheet dismissed before merchant validation', {
          domain: window.location.hostname,
        });
        toast.error(
          t('common:memberClub.payment.toastApplePayInitFailed', {
            defaultValue: 'Apple Pay could not be initialized. Please try card payment.',
          }),
        );
      } else {
        console.warn('[Apple Pay] Payment cancelled by user');
      }
      if (onPaymentComplete) onPaymentComplete();
    };

    session.begin();
  };

  // Attach the click handler to the native <apple-pay-button> custom element.
  // The empty dependency array is intentional: the handler is attached once on
  // mount; the props it closes over (plan, purchaseId, publishableKey, …) are
  // fixed for the lifetime of a checkout dialog, so the stale-closure risk is
  // negligible in practice.
  useEffect(() => {
    const button = applePayButtonRef.current;
    if (button) {
      button.addEventListener('click', handleApplePayClick);

      return () => {
        button.removeEventListener('click', handleApplePayClick);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // dir="ltr": keep the native <apple-pay-button> LTR even when the page is
    // dir="rtl" (Arabic), so Apple's shadow-DOM glyph + text don't flip.
    <div dir="ltr" className="w-full">
      <style>
        {`
          apple-pay-button {
            --apple-pay-button-width: 100%;
            --apple-pay-button-height: 40px;
            --apple-pay-button-border-radius: var(--native-booking-control-radius, 6px);
            --apple-pay-button-padding: 0px 0px;
            --apple-pay-button-box-sizing: border-box;
            display: block;
            margin: 0;
          }
        `}
      </style>
      <apple-pay-button
        ref={(el: HTMLElement | null) => {
          applePayButtonRef.current = el;
        }}
        buttonstyle="black"
        type="pay"
        locale="en"
        data-test="apple-pay-membership-purchase-button"
      />
    </div>
  );
}
